import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Botao } from './base';
import { mensagemDeErro } from '../nucleo/api';

/**
 * Ajustes de 27/10 (C12) e crm4 (L4): a casca da edição rápida de um campo na ficha, como o `editPop` do RD. A linha mostra
 * rótulo e valor; com permissão, clicar abre o editor ali mesmo (o `children`), com Cancelar e Salvar. Esc cancela em
 * qualquer parte; com `enterSalva`, Enter num campo de texto salva. Quem usa decide o editor e o que gravar; o erro lançado
 * por `aoSalvar` aparece como mensagem do campo.
 *
 * Lista de 30/10 (B), item 7.1: saiu do CRM do Cadastros (`modules/cadastros/crm-comum.tsx`) para o núcleo, para a ficha da
 * negociação editar do mesmo jeito que o perfil da empresa (tela de módulo não importa tela de outro módulo).
 */
export function EdicaoRapida({
  rotulo,
  exibir,
  obrigatorio,
  pode,
  aoSalvar,
  aoAbrir,
  apos,
  enterSalva = true,
  children,
  ...dados
}: {
  rotulo: string;
  exibir: ReactNode;
  obrigatorio?: boolean;
  pode: boolean;
  aoSalvar: () => Promise<void>;
  /** Ao abrir o editor: quem usa recarrega o rascunho com o valor atual. */
  aoAbrir: () => void;
  /** Ação ao lado da linha, fora do botão (abrir o site): nada interativo dentro do botão da edição. */
  apos?: ReactNode;
  enterSalva?: boolean;
  /** O editor; recebe o erro do campo (para `aria-invalid`) e o id do rótulo. */
  children: (c: { id: string; erro: string }) => ReactNode;
  [dado: `data-${string}`]: string;
}) {
  const { t: tn } = useTranslation();
  const id = useId();
  const [editando, setEditando] = useState(false);
  const [erro, setErro] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const gatilho = useRef<HTMLButtonElement>(null);
  const editor = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (editando) editor.current?.querySelector<HTMLElement>('input, select, textarea')?.focus();
  }, [editando]);
  const fechar = () => {
    setEditando(false);
    setErro('');
    requestAnimationFrame(() => gatilho.current?.focus());
  };
  const salvar = async () => {
    setOcupado(true);
    try {
      await aoSalvar();
      fechar();
    } catch (e) {
      setErro(mensagemDeErro(e));
    } finally {
      setOcupado(false);
    }
  };
  const vazio = <span className="font-normal text-tinta-3">{tn('comum.naoPreenchido')}</span>;
  if (!editando) {
    const conteudo = (
      <>
        <span className="text-tinta-3">
          {rotulo}
          {obrigatorio && <span className="ml-0.5 text-acento-forte">*</span>}
        </span>
        <span className="min-w-0 text-right font-medium break-words text-tinta-1">{exibir || vazio}</span>
      </>
    );
    const linhaFixa = pode ? (
      <button
        ref={gatilho}
        type="button"
        {...dados}
        aria-label={tn('comum.editarCampo', { campo: rotulo })}
        onClick={() => {
          aoAbrir();
          setEditando(true);
        }}
        className="flex min-h-11 w-full items-center justify-between gap-3 rounded-lg border-b border-borda px-2 py-2 text-left text-sm last:border-0 hover:bg-sup-2"
      >
        {conteudo}
      </button>
    ) : (
      <div className="flex min-h-11 items-center justify-between gap-3 border-b border-borda px-2 py-2 text-sm last:border-0" {...dados}>
        {conteudo}
      </div>
    );
    return apos ? (
      <div className="flex items-center gap-1">
        <div className="min-w-0 flex-1">{linhaFixa}</div>
        {apos}
      </div>
    ) : (
      linhaFixa
    );
  }
  return (
    <div
      ref={editor}
      className="my-1 flex flex-col gap-2 rounded-lg border border-acento-forte bg-sup-1 p-3 shadow-pop"
      data-edicao-rapida=""
      onKeyDown={(e) => {
        // Esc em qualquer parte do editor (também com o foco nos botões) cancela, sem fechar a página ou a gaveta de fora.
        if (e.key === 'Escape') {
          e.stopPropagation();
          fechar();
        } else if (enterSalva && e.key === 'Enter' && (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) && e.target.dataset.enterNaoSalva === undefined) {
          e.preventDefault();
          void salvar();
        }
      }}
    >
      {children({ id, erro })}
      <div className="flex justify-end gap-2">
        <Botao tamanho="sm" variante="fantasma" onClick={fechar}>
          {tn('comum.cancelar')}
        </Botao>
        <Botao tamanho="sm" variante="primario" carregando={ocupado} onClick={() => void salvar()} data-salvar-edicao="">
          {tn('comum.salvar')}
        </Botao>
      </div>
    </div>
  );
}
